import React from 'react';
import { Eye } from 'lucide-react';
import { useTheme } from '../../themes/ThemeProvider';
import { useTranslation } from '../../i18n/useTranslation';
import { tokens } from '../../styles/designTokens';
import InitialPlaceholder from '../core/InitialPlaceholder';
import MoreButton from '../frontpage/columns/MoreButton';

// cover_image_url is the source of truth for card thumbnails WHEN PRESENT — it is not
// guaranteed. Video articles auto-derive it, but a website article whose owner skipped the
// screenshot has none; imageless cards fall back to the gradient/initial placeholder below.
const getArticleImageSrc = (article) => article.cover_image_url || null;

// 1x1 light-grey SVG used as fallback when an article image fails to load.
// Inline data-URI avoids any network request (and service-worker retry loops).
const FALLBACK_IMAGE = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='250' height='186' fill='%23e5e7eb'%3E%3Crect width='250' height='186'/%3E%3C/svg%3E";

const NewsGrid = ({
  onArticleClick,
  articles,
  isMobile = false,
  selectedGroup = null,
  // Load More (Roadmap #104, addendum 5): { hasMore, onLoadMore, loading } or null. Only the
  // locality News list passes it; every other mount of NewsGrid is unchanged. News stays in rank
  // order, so the next 50 appear below the ones already shown.
  paging = null,
  pagingBusy = false
}) => {
  const themeConfig = useTheme();
  const { t, formattingLocale } = useTranslation('ui');
  const loadMore = paging ? (
    <MoreButton
      hasMore={paging.hasMore}
      onClick={paging.onLoadMore}
      label={paging.loading ? t('common:loading') : t('common:load_more')}
      color={themeConfig.primary}
      iconName="ChevronDown"
      disabled={pagingBusy}
    />
  ) : null;
  // Show all articles (text and video types) - NewsGrid displays the news feed
  const newsArticles = articles || [];

  // Desktop card (250x250px with image + metadata). ArticleDetail's news slot reuses this
  // whole NewsGrid component (July 2026) rather than a separate card, so card consistency is
  // guaranteed by identity, not by a discipline of keeping two implementations in sync.
  const DesktopNewsCard = ({ article, onClick }) => (
    <div
      className="news-card"
      onClick={() => onClick(article.id)}
      style={{
        width: '250px',
        height: '250px',
        position: 'relative',
        cursor: 'pointer',
        borderRadius: '10px',
        overflow: 'hidden',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        transition: 'transform 0.2s, box-shadow 0.2s'
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateY(-2px)';
        e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)';
      }}
    >
      <img
        src={getArticleImageSrc(article) || 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7'}
        alt={article.title}
        loading="lazy"
        style={{
          width: '100%',
          height: '186px',
          objectFit: 'cover'
        }}
        onError={(e) => {
          e.target.onerror = null;
          e.target.src = FALLBACK_IMAGE;
        }}
      />

      <div style={{
        position: 'absolute',
        bottom: '0',
        left: '0',
        right: '0',
        height: '64.5px',
        background: 'white',
        padding: '5px'
      }}>
        {/* Author, Date, and View Count on same line */}
        <div style={{
          display: 'flex',
          gap: '4px',
          alignItems: 'center',
          marginBottom: '1px'
        }}>
          <span style={{
            color: '#64748B',
            fontSize: '8px',
            fontFamily: 'OpenSauceSans',
            fontWeight: '700'
          }}>
            {article.author_name || article.author || t('unknown_author')}
          </span>
          <span style={{
            color: themeConfig?.primary || '#024B99',
            fontSize: '8px',
            fontFamily: 'OpenSauceSans',
            fontWeight: '900'
          }}>
            {article.date || new Date(article.created_at).toLocaleDateString(formattingLocale)}
          </span>
          {article.view_count > 0 && (
            <span style={{
              display: 'flex',
              alignItems: 'center',
              gap: '2px',
              color: '#64748B',
              fontSize: '8px',
              fontFamily: 'OpenSauceSans',
              fontWeight: '600',
              marginLeft: 'auto'
            }}>
              <Eye size={10} />
              {article.view_count}
            </span>
          )}
        </div>

        {(article.group_name || article.nearest_locality) && (
          <div style={{
            color: themeConfig?.primary || '#024B99',
            fontSize: '7px',
            fontFamily: 'OpenSauceSans',
            fontWeight: '700',
            marginBottom: '1px',
            overflow: 'hidden',
            whiteSpace: 'nowrap',
            textOverflow: 'ellipsis'
          }}>
            {article.group_name || article.nearest_locality}
          </div>
        )}

        <div style={{
          color: '#1E293B',
          fontSize: '12px',
          fontFamily: 'OpenSauceSans',
          fontWeight: '700',
          lineHeight: '14px',
          overflow: 'hidden',
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical'
        }}>
          {article.title}
        </div>
      </div>
    </div>
  );

  // Mobile card - fills grid cell width, maintains square aspect ratio
  const MobileNewsCard = ({ article, onClick }) => (
    <div
      className="news-card-mobile"
      onClick={() => onClick(article.id)}
      style={{
        width: '100%',
        aspectRatio: '1 / 1',
        position: 'relative',
        cursor: 'pointer',
        borderRadius: '8px',
        overflow: 'hidden',
        boxShadow: '0 2px 6px rgba(0,0,0,0.1)',
        backgroundColor: '#FFFFFF',
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      {/* Image section - 75% of card height (4:3 within square) */}
      <div style={{
        flex: '3',
        minHeight: 0,
        overflow: 'hidden',
        position: 'relative'
      }}>
        {getArticleImageSrc(article) ? (
          <img
            src={getArticleImageSrc(article)}
            alt={article.title}
            loading="lazy"
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover'
            }}
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = FALLBACK_IMAGE;
            }}
          />
        ) : (
          <InitialPlaceholder name={article.title} fontSize="2.5rem" style={{ width: '100%', height: '100%' }} />
        )}
        {article.view_count > 0 && (
          <div style={{
            position: 'absolute',
            top: '6px',
            right: '6px',
            display: 'flex',
            alignItems: 'center',
            gap: '3px',
            padding: '3px 6px',
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            borderRadius: '4px',
            color: 'white',
            fontSize: '10px',
            fontFamily: 'OpenSauceSans',
            fontWeight: '600'
          }}>
            <Eye size={10} />
            {article.view_count}
          </div>
        )}
      </div>

      {/* Headline section - 25% of card height */}
      <div style={{
        flex: '1',
        minHeight: 0,
        padding: '8px 10px',
        display: 'flex',
        alignItems: 'center',
        backgroundColor: '#FFFFFF'
      }}>
        <div style={{
          color: '#1E293B',
          fontSize: '12px',
          fontFamily: 'OpenSauceSans, sans-serif',
          fontWeight: '700',
          lineHeight: '1.3',
          overflow: 'hidden',
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical'
        }}>
          {article.title}
        </div>
      </div>
    </div>
  );

  const handleCardClick = (article) => {
    if (onArticleClick) {
      onArticleClick(article.id);
    }
  };

  // Mobile layout — scrollable grid, no column header (Row 2 carries the create button)
  if (isMobile) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <div style={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          overflowX: 'hidden',
          scrollbarWidth: 'thin',
          paddingRight: '2px'
        }}>
          <div style={{
            padding: '10px',
            paddingBottom: '80px',
            boxSizing: 'border-box'
          }}>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 160px))',
              gap: '10px',
              justifyContent: 'center',
              alignItems: 'start'
            }}>
              {newsArticles.length === 0 ? (
                <div style={{
                  gridColumn: '1 / -1',
                  textAlign: 'center',
                  padding: '40px 20px',
                  color: tokens.colors.textSecondary,
                  fontSize: '14px'
                }}>
                  {t('no_articles_found')}
                </div>
              ) : (
                <>
                  {/* Group image as first card on mobile */}
                  {selectedGroup?.logo_url && (
                    <div style={{
                      width: '100%',
                      aspectRatio: '1 / 1',
                      borderRadius: '8px',
                      overflow: 'hidden',
                      boxShadow: '0 2px 6px rgba(0,0,0,0.1)',
                      backgroundColor: '#FFFFFF'
                    }}>
                      <img
                        src={selectedGroup.logo_url}
                        alt={selectedGroup.name}
                        loading="lazy"
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover'
                        }}
                      />
                    </div>
                  )}
                  {newsArticles.map((article) => (
                    <MobileNewsCard
                      key={article.id}
                      article={article}
                      onClick={() => handleCardClick(article)}
                    />
                  ))}
                </>
              )}
            </div>
            {loadMore}
          </div>
        </div>
      </div>
    );
  }

  // Desktop layout (original)
  return (
    <div
      style={{
        width: '100%',
        height: '100%',
        backgroundColor: 'transparent',
        overflowY: 'auto',
        overflowX: 'hidden',
        position: 'relative'
      }}
    >
      <div
        style={{
          // No viewport arithmetic here — NewsGrid is always embedded in a parent that already
          // constrains its height (the outer wrapper above already inherits height:100%). A
          // reusable component should fill the slot it's given, not know about the viewport;
          // this used to be minHeight:'calc(100vh - 200px)', which only ever worked by
          // coincidence (200 = 80+120, the same desktop chrome the parent already subtracts).
          position: 'absolute',
          top: '20px',
          left: '20px',
          width: 'calc(100% - 40px)',
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, 250px)',
          gridAutoRows: '250px',
          gap: '20px',
          justifyItems: 'center',
          alignItems: 'center',
          justifyContent: 'start',
          alignContent: 'start',
          paddingBottom: '40px'
        }}
      >
        {newsArticles.length === 0 ? (
          <div style={{
            gridColumn: '1 / -1',
            textAlign: 'center',
            padding: '40px',
            color: '#6b7280',
            fontSize: '16px'
          }}>
            {t('no_articles_found')}
          </div>
        ) : (
          newsArticles.map((article) => (
            <DesktopNewsCard
              key={article.id}
              article={article}
              onClick={() => handleCardClick(article)}
            />
          ))
        )}
        {loadMore && newsArticles.length > 0 && (
          <div style={{ gridColumn: '1 / -1', alignSelf: 'start', width: '100%' }}>{loadMore}</div>
        )}
      </div>
    </div>
  );
};

export default NewsGrid;
